import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import { bindActionCreators } from 'redux';
import Papa from 'papaparse';
import {
  Grid, Button, IconButton, Typography,
  ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import * as snackbarActions from '../../actions/snackbar';
import taskActions from '../../actions/tasks';
import wranglerActions from '../../actions/wranglers';
import vehicleActions from '../../actions/vehicles';
import locationActions from '../../actions/locations';
import vehicleZoneActions from '../../actions/vehicleZones';
import pickupZoneActions from '../../actions/pickupZones';
import {
  DEPLOYMENT_STATUSES,
  getDeploymentStatusReadable,
  POSSESSION_STATUSES,
  getPossessionStatusReadable,
  EXT_STATUSES,
  getExtStatusReadable,
  WranglerLoginStatuses,
  searchMultipleVehicles,
  filterVehicles,
  TaskTypes,
  getTitleAndDataCount,
  PICKUP_FIELDS,
} from '../../lib/utils';
import { getStatusFilters, getAdvancedFilters } from '../../lib/filter-utils';
import VehicleFilterChips from './vehicles-page/vehicle-filter-chips';
import LinkMapFilters from './vehicles-page/link-map-filters';
import LinkVehicleMap from './vehicles-page/link-vehicle-map';
import { VehicleTable } from './vehicle-table';
import LinkTableFiltersModal from './vehicles-page/link-table-filters-modal';
import MultiSelect from '../common/multi-select';
import TaskFormContainer from './tasks';
import ActionsDropdownMenu from './actions-dropdown-menu';
import BulkVehicleStatus from '../common/bulk-vehicle-status';
import { BulkAssignModal } from './vehicles-page/bulk-assign-task-modal';
import VehicleEditForm from './vehicle-edit-form';
import TableFilterList from '../common/table-filter-list';

const STORAGE_KEY = 'VehicleFilterChipSettings';
const LIST_TAB = 'list';
const MAP_TAB = 'map';
const UNASSIGN_OPTION = 'Unassigned';

const styles = {
  filterSection: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
};

class MarketVehicleSummary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      skipInitialStateLoad: false,
      ...getStatusFilters(),
      tabSelected: LIST_TAB,
      mapOnlyFilterSettings: {},
      showFilterModal: false,
      searchText: undefined,
      advancedFilters: {},
      isClustered: false,
      polygonVehicles: undefined,
      selectedRows: [],
      selectedVehicleIds: [],
      taskIds: [],
      showEditForm: false,
      showBulkAssignModal: false,
      showBulkVehicleStatus: false,
      lastUpdateTime: new Date(),
      editVehicle: {},
      showHistory: false,
    };

    this.setFiltersFromUrl();

    this.setSearchText = this.setSearchText.bind(this);
    this.setSelectedVehicleIds = this.setSelectedVehicleIds.bind(this);
    this.handleRowsSelected = this.handleRowsSelected.bind(this);
    this.handleFiltersChange = this.handleFiltersChange.bind(this);
    this.saveLocalState = this.saveLocalState.bind(this);
    this.handleUpdateLocationTarget = this.handleUpdateLocationTarget.bind(this);
    this.handleAddTask = this.handleAddTask.bind(this);
    this.openBulkStatus = this.openBulkStatus.bind(this);
    this.saveBulkStatus = this.saveBulkStatus.bind(this);
    this.closeBulkStatus = this.closeBulkStatus.bind(this);
    this.openBulkAssign = this.openBulkAssign.bind(this);
    this.closeBulkAssign = this.closeBulkAssign.bind(this);
    this.saveBulkAssign = this.saveBulkAssign.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.onUploadClick = this.onUploadClick.bind(this);
    this.onUpload = this.onUpload.bind(this);
    this.openVehicleForm = this.openVehicleForm.bind(this);
    this.handleCloseVehicleForm = this.handleCloseVehicleForm.bind(this);
    this.handleSaveVehicleForm = this.handleSaveVehicleForm.bind(this);
  }

  componentDidMount() {
    const { skipInitialStateLoad } = this.state;
    const prevState = JSON.parse(localStorage.getItem(STORAGE_KEY));

    if (!skipInitialStateLoad && prevState) {
      this.setState(() => ({ ...prevState, skipInitialStateLoad: false }));
    }
    this.fetchData();
  }

  componentWillUnmount() {
    clearTimeout(this.updateIntervalId);
  }

  setFiltersFromUrl() {
    const searchParams = new URLSearchParams(window.location.search);

    const parseArrayFilter = (name, isAdvanced) => {
      const value = searchParams.get(name);

      if (isAdvanced) {
        this.state.advancedFilters[`${name}Filter`] = value
          ? value.split(',')
          : [];
      } else if (value !== null) {
        this.state[`${name}Filters`] = value.split(',');
      }
    };

    const parseValueFilter = (name, defaultValue) => {
      const value = searchParams.get(name);
      this.state.advancedFilters[`${name}Filter`] = value || defaultValue;
    };

    const parseDateFilter = (name, type) => {
      const typeName = type ? 'After' : 'Before';
      const filterName = `last${name[0].toUpperCase()}${name.slice(1)}Filter${typeName}`;
      const value = searchParams.get(`${name}${typeName}`);

      if (value) {
        this.state.advancedFilters[filterName] = new Date(parseInt(value, 10));
      }
    };

    /* Main filters */
    parseArrayFilter('deployment', false);
    parseArrayFilter('possession', false);
    parseArrayFilter('extStatus', false);

    /* Advanced filters */
    parseArrayFilter('fleet', true);
    parseArrayFilter('geofenceLayers', true);
    parseArrayFilter('taskAssignees', true);
    parseArrayFilter('pickupReasons', true);
    parseArrayFilter('pickupZones', true);
    parseArrayFilter('inspectReasons', true);
    parseArrayFilter('hardware', true);
    parseArrayFilter('scooterType', true);
    parseArrayFilter('lastReportedNetwork', true);

    parseValueFilter('pickupFields', PICKUP_FIELDS.ALL_VEHICLES);
    parseValueFilter('location', '');
    parseValueFilter('maintenance', '');
    parseValueFilter('hasDefaultGpkg', '');
    parseValueFilter('minBattery', 0);
    parseValueFilter('maxBattery', 100);
    parseValueFilter('minSearchAttempts', 0);
    parseValueFilter('maxSearchAttempts', undefined);

    parseDateFilter('communication', 1);
    parseDateFilter('communication', 0);
    parseDateFilter('deploy', 1);
    parseDateFilter('deploy', 0);
    parseDateFilter('pickup', 1);
    parseDateFilter('pickup', 0);
    parseDateFilter('riderScan', 1);
    parseDateFilter('riderScan', 0);
    parseDateFilter('trip', 1);
    parseDateFilter('trip', 0);
  }

  updateUrlWithFilters() {
    const newURL = new URL(window.location.href);
    const searchParams = new URLSearchParams();

    const setArrayFilter = (name, defaultValue, isAdvanced) => {
      const filterValue = (isAdvanced
        ? this.state.advancedFilters[`${name}Filter`]
        : this.state[`${name}Filters`]) || [];

      if (!isEqual(sortBy(filterValue), sortBy(defaultValue))) {
        searchParams.set(name, filterValue.join(','));
      }
    };

    const setValueFilter = (name, defaultValue = '') => {
      const filterValue = this.state.advancedFilters[`${name}Filter`] || '';

      if (filterValue && filterValue !== defaultValue) {
        searchParams.set(name, filterValue);
      }
    };

    const setDateFilter = (name, type) => {
      const filterName = `last${name[0].toUpperCase()}${name.slice(1)}Filter${
        type ? 'After' : 'Before'}`;
      const filterValue = this.state.advancedFilters[filterName];

      if (filterValue) {
        searchParams.set(`${name}${type ? 'After' : 'Before'}`, filterValue.getTime());
      }
    };

    /* Main filters */
    setArrayFilter('deployment', DEPLOYMENT_STATUSES, false);
    setArrayFilter('possession', POSSESSION_STATUSES, false);
    setArrayFilter('extStatus', EXT_STATUSES, false);

    /* Advanced filters */
    setArrayFilter('fleet', [], true);
    setArrayFilter('geofenceLayers', [], true);
    setArrayFilter('taskAssignees', [], true);
    setArrayFilter('pickupReasons', [], true);
    setArrayFilter('pickupZones', [], true);
    setArrayFilter('inspectReasons', [], true);
    setArrayFilter('hardware', [], true);
    setArrayFilter('scooterType', [], true);
    setArrayFilter('lastReportedNetwork', [], true);

    setValueFilter('pickupFields', PICKUP_FIELDS.ALL_VEHICLES);
    setValueFilter('location');
    setValueFilter('maintenance');
    setValueFilter('hasDefaultGpkg');
    setValueFilter('minBattery', 0);
    setValueFilter('maxBattery', 100);
    setValueFilter('minSearchAttempts', 0);
    setValueFilter('maxSearchAttempts');

    setDateFilter('communication', 1);
    setDateFilter('communication', 0);
    setDateFilter('deploy', 1);
    setDateFilter('deploy', 0);
    setDateFilter('pickup', 1);
    setDateFilter('pickup', 0);
    setDateFilter('riderScan', 1);
    setDateFilter('riderScan', 0);
    setDateFilter('trip', 1);
    setDateFilter('trip', 0);

    newURL.search = searchParams.toString();

    window.history.replaceState({ path: newURL.href }, '', newURL.href);
  }

  handleFiltersChange(type, filters) {
    const filtersName = `${type}Filters`;

    this.setState(
      { [filtersName]: filters },
      this.updateUrlWithFilters,
    );
  }

  get filteredVehicles() {
    let vehicles = get(this, 'props.vehicles', []);
    const { vehicleActivities } = this.props;
    const {
      searchText,
      deploymentFilters,
      possessionFilters,
      extStatusFilters,
      advancedFilters,
      polygonVehicles,
    } = this.state;
    // filter vehicles list down to displayed vehicles
    const filters = {
      deploymentFilters,
      possessionFilters,
      extStatusFilters,
      ...advancedFilters,
    };

    if (searchText
        && polygonVehicles
        && polygonVehicles.length > 0
        && searchText.length > 0
    ) {
      vehicles = searchMultipleVehicles(vehicles, searchText);
      vehicles = filterVehicles(vehicles, filters, vehicleActivities);
    } else if (searchText && searchText.length > 0) {
      vehicles = searchMultipleVehicles(vehicles, searchText);
      vehicles = filterVehicles(vehicles, filters, vehicleActivities);
    } else if (polygonVehicles && polygonVehicles.length > 0) {
      vehicles = filterVehicles(polygonVehicles, filters, vehicleActivities);
    } else {
      vehicles = filterVehicles(vehicles, filters, vehicleActivities);
    }
    return vehicles;
  }

  get filteredLocations() {
    const locations = get(this, 'props.locations', []);
    const { mapOnlyFilterSettings } = this.state;
    const { hubFilter } = mapOnlyFilterSettings;
    return hubFilter ? locations : [];
  }

  get filteredVehicleZones() {
    const vehicleZones = get(this, 'props.vehicleZones', []);
    const pickupZones = get(this, 'props.pickupZones', []);
    const { mapOnlyFilterSettings } = this.state;
    const { geofenceFilter, pickupZoneFilter } = mapOnlyFilterSettings;
    if (geofenceFilter && pickupZoneFilter) return vehicleZones.concat(pickupZones);
    else if (geofenceFilter) return vehicleZones;
    else if (pickupZoneFilter) return pickupZones;
    else return [];
  }

  get filteredWranglers() {
    const wranglers = get(this, 'props.wranglers', []);
    return wranglers.filter(
      (wrangler) => get(wrangler, 'loginStatus') === WranglerLoginStatuses.LOGGED_IN,
    );
  }

  get getSelectedVehicles() {
    const { vehicles } = this.props;
    const { selectedVehicleIds } = this.state;

    return vehicles.filter((vehicle) => includes(selectedVehicleIds, vehicle._id));
  }

  fetchData = () => {
    const {
      fetchVehicles,
      fetchVehicleActivities,
      fetchLocations,
      fetchVehicleZones,
      fetchPickupZones,
      fetchWranglers,
      match,
    } = this.props;

    const marketId = get(match, 'params.marketId');

    if (marketId) {
      fetchVehicles({ market_id: marketId });
      fetchVehicleActivities(marketId);
      fetchLocations({ market_id: marketId });
      fetchVehicleZones(marketId);
      fetchPickupZones(marketId);
      fetchWranglers({ market_id: marketId });

      this.setState({ lastUpdateTime: new Date() });
    }

    clearTimeout(this.updateIntervalId);

    this.updateIntervalId = setInterval(
      this.fetchData,
      process.env.REACT_APP_VEHICLES_UPDATE_INTERVAL_MS || 300000, // 5 minutes default
    );
  };

  setSelectedVehicleIds(ids) {
    this.setState({ selectedVehicleIds: ids });
  }

  setSearchText(text) {
    const { vehicles } = this.props;

    this.setState({ searchText: text });

    const vehicleIds = vehicles.filter((vehicle) => text.includes(vehicle.humanId))
      .map((vehicle) => vehicle._id);
    this.setState({ selectedVehicleIds: vehicleIds });
  }

  handleRowsSelected(rows, ids) {
    this.setState({ selectedRows: rows, selectedVehicleIds: ids });
  }

  handleUpdateLocationTarget(update, locId) {
    const {
      updateLocation, fetchLocations, match, showSuccessSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    updateLocation(update, locId).then((res) => {
      const name = get(res, 'data.name', '');
      const target = get(res, 'data.targetVehicleCounts.link', '');
      if (get(res, 'status') === 200) {
        showSuccessSnackbar(
          `Succesfully updated target for ${name} to ${target}`,
        );
        fetchLocations({ market_id: marketId });
      }
    });
  }

  handleAddTask() {
    const { marketId } = get(this, 'props.match.params');
    const { setOneTask } = this.props;
    const { selectedVehicleIds } = this.state;

    const taskData = {
      marketId,
      assignedVehicleIds: selectedVehicleIds,
      task: TaskTypes.PICK_UP,
    };
    setOneTask(taskData);
  }

  openBulkAssign() {
    const vehiclesWithTasks = this.getSelectedVehicles.filter(
      (vehicle) => vehicle.tasks && vehicle.tasks.length > 0,
    );
    const allTasksIds = flatten(
      vehiclesWithTasks.map((vehicle) => vehicle.tasks.map((task) => task._id)),
    );
    this.setState({ showBulkAssignModal: true, taskIds: allTasksIds });
  }

  saveBulkAssign(event, taskIds, assignedToId) {
    // stopProp needed because there is also an onRowClick listener
    if (event) { event.stopPropagation(); }

    const {
      match,
      bulkUpdateTaskOwner,
      fetchVehicles,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    if (!taskIds || !assignedToId) return;

    bulkUpdateTaskOwner(taskIds, assignedToId)
      .then(() => showSuccessSnackbar(`Successfully assigned wrangler to ${taskIds.length} task(s)`))
      .then(() => fetchVehicles(query))
      .catch((error) => showErrorSnackbar(`Error from server ${error}`));
  }

  saveBulkStatus(deploymentStatus, possessionStatus) {
    const {
      match,
      bulkUpdateStatus,
      fetchVehicles,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const selectedVehicles = this.getSelectedVehicles;
    const selectedExtId = selectedVehicles.map((vehicle) => get(vehicle, 'extId'));
    const selectedHumanId = selectedVehicles.map((vehicle) => get(vehicle, 'humanId'));

    const stateUpdate = {
      showBulkVehicleStatus: false,
    };

    bulkUpdateStatus(selectedExtId, deploymentStatus, possessionStatus)
      .then(({ data }) => {
        const { successes, failures } = data;
        const total = (successes || 0) + (failures || 0);
        this.setState(stateUpdate, () => {
          let deploymentString = '';
          let possessionString = '';
          if (deploymentStatus) {
            deploymentString = (`${successes}/${total} Vehicle(s) ${selectedHumanId.join(', ')} updated to ${deploymentStatus}`);
          }
          if (possessionStatus) {
            possessionString = (`${successes}/${total} Vehicle(s) ${selectedHumanId.join(', ')} updated to ${possessionStatus}`);
          }
          if (successes > 0) {
            showSuccessSnackbar(deploymentString + possessionString);
          } else {
            showErrorSnackbar(deploymentString + possessionString);
          }
        });
      })
      .then(() => fetchVehicles(query))
      .catch((error) => {
        this.setState(stateUpdate, () => {
          showErrorSnackbar(`Error from server ${error}`);
        });
      });
  }

  closeBulkAssign() {
    this.setState({ showBulkAssignModal: false });
  }

  openBulkStatus() {
    this.setState({ showBulkVehicleStatus: true });
  }

  closeBulkStatus() {
    this.setState({ showBulkVehicleStatus: false });
  }

  handleDropdownChange = (event) => {
    const selected = event.target.getAttribute('name');
    switch (selected) {
      case 'Request Pickup':
        this.handleAddTask();
        break;
      case 'Change Status':
        this.openBulkStatus();
        break;
      case 'Assign Task':
        this.openBulkAssign();
        break;
      default:
        break;
    }
  };

  onUploadClick = () => {
    const input = document.getElementById('file-input');
    if (input) {
      input.click();
    }
  }

  onUpload = (e) => {
    const { showErrorSnackbar } = this.props;

    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = results.data;
          const shortcodes = rows
            .map((vehicle) => vehicle.Shortcode || vehicle.shortcode)
            .filter((v) => v);
          if (shortcodes.length > 1) {
            this.setSearchText(shortcodes);
          } else {
            showErrorSnackbar('No shortcodes found in file');
          }
        },
      });
    } else {
      showErrorSnackbar('An error occured with file upload');
    }
  }

  saveLocalState() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
  }

  handleCloseVehicleForm() {
    this.setState({
      showEditForm: false,
      editVehicle: {},
    });
  }

  openVehicleForm(vehicle, showHistory = false) {
    this.setState({
      showEditForm: true,
      editVehicle: vehicle,
      showHistory,
    });
  }

  handleSaveVehicleForm(vehicle) {
    const {
      match,
      updateVehicle,
      fetchVehicles,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const stateUpdate = {
      showEditForm: false,
      editVehicle: {},
    };
    updateVehicle(vehicle, vehicle._id)
      .then(() => {
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Successfully updated vehicle ${vehicle.humanId}`);
        });
      })
      .then(() => fetchVehicles(query))
      .catch((error) => {
        this.setState(stateUpdate, () => {
          const errorResponseMsg = get(error, 'message') || error.toString();
          showErrorSnackbar(`Error updating vehicle ${vehicle.humanId}: ${errorResponseMsg}`);
        });
      });
  }

  render() {
    const {
      vehiclesLoading,
      vehicleActivities,
      oneTask,
      fetchOneTask,
      setOneTask,
      wranglers,
      adminUrls,
    } = this.props;
    const {
      searchText,
      deploymentFilters,
      possessionFilters,
      extStatusFilters,
      tabSelected,
      showFilterModal,
      selectedVehicleIds,
      taskIds,
      showBulkAssignModal,
      showBulkVehicleStatus,
      showEditForm,
      editVehicle,
      showHistory,
      advancedFilters,
    } = this.state;

    const sortedVehicles = sortBy(get(this, 'props.vehicles', []), 'humanId');
    const vehicleOptions = sortedVehicles.map((vehicle) => ({
      value: vehicle.humanId,
      label: vehicle.humanId,
      isDisabled: false,
    }));

    const pickupZones = sortBy(get(this, 'props.pickupZones', []), 'name');

    const wranglersById = keyBy(wranglers, '_id');
    const sortedWranglers = sortBy(wranglersById, (wrangler) => wrangler.firstName.toLowerCase());
    const wranglerOptions = sortedWranglers.map((wrangler) => ({
      value: wrangler._id,
      label: `${wrangler.firstName} ${wrangler.lastName}`,
    }));
    wranglerOptions.unshift({ value: UNASSIGN_OPTION, label: UNASSIGN_OPTION });

    const childMarkets = sortedVehicles
      .map((vehicle) => vehicle.childMarket)
      .filter((market) => market);
    const uniqueChildMarkets = [...new Map(childMarkets.map((market) => (
      [market._id, market]
    ))).values()];

    const cellNetworks = sortedVehicles
      .map((vehicle) => vehicle.lastReportedNetwork)
      .filter((network) => network);
    const uniqueCellNetworks = [...new Set(cellNetworks)];

    return (
      <Grid>
        <Grid container justifyContent="flex-start" style={styles.filterSection} sx={{ my: 1 }}>
          <LinkTableFiltersModal
            open={showFilterModal}
            onClose={() => {
              this.setState({ showFilterModal: false });
            }}
            onChange={(filters) => this.handleFiltersChange('advanced', filters)}
            pickupZones={pickupZones}
            childMarkets={uniqueChildMarkets}
            cellNetworks={uniqueCellNetworks}
            filters={advancedFilters}
            wranglerOptions={wranglerOptions}
            defaultFilters={getAdvancedFilters()}
          />
          {!(this.state.isClustered && tabSelected === MAP_TAB) && (
            <Grid item container alignItems="flex-start">
              <Grid item xs={12} md={9}>
                <Grid item>
                  <VehicleFilterChips
                    onChange={(filters) => this.handleFiltersChange('deployment', filters)}
                    chipValues={DEPLOYMENT_STATUSES}
                    getChipLabel={getDeploymentStatusReadable}
                    selectedFilters={deploymentFilters}
                    toggleAllLabel="Toggle All"
                    rowLabel="Deployments"
                  />
                </Grid>
                <Grid item sx={{ my: 1 }}>
                  <VehicleFilterChips
                    onChange={(filters) => this.handleFiltersChange('possession', filters)}
                    chipValues={POSSESSION_STATUSES}
                    getChipLabel={getPossessionStatusReadable}
                    selectedFilters={possessionFilters}
                    toggleAllLabel="Toggle All"
                    rowLabel="Possessions"
                  />
                </Grid>
                <Grid item>
                  <VehicleFilterChips
                    onChange={(filters) => this.handleFiltersChange('extStatus', filters)}
                    chipValues={EXT_STATUSES}
                    getChipLabel={getExtStatusReadable}
                    selectedFilters={extStatusFilters}
                    toggleAllLabel="Toggle All"
                    rowLabel="States"
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                spacing="2"
                alignItems="center"
                xs={12}
                md={3}
                sx={{ zIndex: 101, mt: { xs: 2, md: 0 } }}
              >
                <Grid item flexGrow="1">
                  <MultiSelect
                    placeholder="Select Vehicle(s)"
                    value={searchText || []}
                    options={vehicleOptions}
                    handleChange={(value) => this.setSearchText(value)}
                  />
                </Grid>
                <Grid item sx={{ display: { xs: 'none', lg: 'flex' } }}>
                  <Tooltip title="Upload Shortcodes">
                    <IconButton onClick={this.onUploadClick}>
                      <CloudUploadIcon />
                    </IconButton>
                  </Tooltip>
                  <input
                    type="file"
                    id="file-input"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={this.onUpload}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} sx={{ my: 1 }}>
            <Grid container alignItems="center">
              <Grid item>
                <ToggleButtonGroup
                  exclusive
                  value={tabSelected}
                  onChange={(event, selected) => this.setState({ tabSelected: selected })}
                >
                  <ToggleButton value={LIST_TAB}>List</ToggleButton>
                  <ToggleButton value={MAP_TAB}>Map</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item sx={{ ml: { xs: 'auto', sm: 3 } }}>
                {tabSelected === MAP_TAB && (
                  <Button
                    type="outlined"
                    color="primary"
                    size="small"
                    sx={{ width: { xs: 90, sm: 'auto' } }}
                    onClick={() => this.setState(({ isClustered }) => ({
                      isClustered: !isClustered,
                    }))}
                  >
                    {this.state.isClustered ? 'Atomized' : 'Clustered'}
                    {' '}
                    View
                  </Button>
                )}
                {!(tabSelected === MAP_TAB && this.state.isClustered) && (
                  <Button
                    size="small"
                    type="outlined"
                    color="primary"
                    sx={{ width: { xs: 90, sm: 'auto' } }}
                    onClick={() => this.setState({ showFilterModal: true })}
                  >
                    Advanced Filters
                  </Button>
                )}
              </Grid>
              {tabSelected === MAP_TAB && (
                <Grid item md={6} sx={{ ml: { xs: 0, sm: 6 } }}>
                  <LinkMapFilters
                    onClick={(settings) => {
                      this.setState({ mapOnlyFilterSettings: settings });
                    }}
                  />
                </Grid>
              )}
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <TableFilterList
                  filterList={Object.entries(advancedFilters)}
                  filterUpdate={(filters) => this.handleFiltersChange('advanced', filters)}
                  advancedFilters={advancedFilters}
                  defaultFilters={getAdvancedFilters()}
                  pickupZones={pickupZones.map((zone) => ({ value: zone._id, label: zone.name }))}
                  childMarkets={uniqueChildMarkets.map(
                    (market) => ({ value: market._id, label: market.name }),
                  )}
                  wranglers={wranglers}
                />
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Grid>
                  {getTitleAndDataCount('Vehicles', this.filteredVehicles)}
                </Grid>
                <ActionsDropdownMenu
                  onChange={this.handleDropdownChange}
                  options={['Request Pickup', 'Change Status', 'Assign Task']}
                />
              </Grid>
              <Grid container alignItems="center" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                <Typography variant="caption">
                  Last updated at:
                  {' '}
                  {this.state.lastUpdateTime.toLocaleString()}
                </Typography>
                <IconButton
                  size="small"
                  sx={{ ml: 0.5, p: 0.5 }}
                  onClick={this.fetchData}
                >
                  <RefreshIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {tabSelected === LIST_TAB && (
          <VehicleTable
            vehicles={this.filteredVehicles}
            vehicleActivities={vehicleActivities}
            vehiclesLoading={vehiclesLoading}
            selectedRows={this.state.selectedRows}
            handleRowsSelected={this.handleRowsSelected}
            openVehicleForm={this.openVehicleForm}
          />
        )}
        {tabSelected === MAP_TAB && (
          <LinkVehicleMap
            isClustered={this.state.isClustered}
            vehicles={this.filteredVehicles}
            locations={this.filteredLocations}
            vehicleZones={this.filteredVehicleZones}
            wranglers={this.filteredWranglers}
            selectedMarket={get(this, 'props.selectedMarket', {})}
            onUpdateLocationTarget={this.handleUpdateLocationTarget}
            fetchOneTask={fetchOneTask}
            setOneTask={setOneTask}
            updatePolygonVehicles={(value) => {
              this.setState({ polygonVehicles: value, searchText: [] });
            }}
            setSelectedVehicleIds={this.setSelectedVehicleIds}
            setSearchText={this.setSearchText}
            openVehicleForm={this.openVehicleForm}
          />
        )}
        {oneTask && oneTask.assignedVehicleIds && (
          <TaskFormContainer
            editing={!!oneTask._id}
            bulk={oneTask.assignedVehicleIds.length > 1}
            task={oneTask}
          />
        )}
        {this.filteredVehicles && (
          <BulkVehicleStatus
            count={selectedVehicleIds.length}
            open={showBulkVehicleStatus}
            handleClose={this.closeBulkStatus}
            handleSave={this.saveBulkStatus}
          />
        )}
        {wranglerOptions && (
          <BulkAssignModal
            taskIds={taskIds}
            open={showBulkAssignModal}
            wranglerOptions={wranglerOptions}
            handleCloseModal={this.closeBulkAssign}
            handleSave={this.saveBulkAssign}
          />
        )}
        <VehicleEditForm
          key={`vehicle-edit-form-${editVehicle._id}`}
          isOpen={showEditForm}
          onCancel={this.handleCloseVehicleForm}
          onSave={this.handleSaveVehicleForm}
          vehicle={editVehicle}
          adminUrls={adminUrls}
          showHistory={showHistory}
        />
      </Grid>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    ...vehicleActions,
    ...wranglerActions,
    ...locationActions,
    ...vehicleZoneActions,
    ...pickupZoneActions,
    ...taskActions,
    ...snackbarActions,
  },
  dispatch,
);

const mapStateToProps = (
  {
    markets, vehicles, locations, vehicleZones, wranglers, tasks, pickupZones, settings,
  },
  { match },
) => {
  const marketId = get(match, 'params.marketId');
  const vehicleActivities = get(vehicles, 'activities');
  const marketVehicleActivities = get(vehicleActivities, marketId);

  return {
    selectedMarket: markets.selectedMarket,
    marketLostDeletedVehicles: vehicles.marketLostDeletedVehicles,
    vehicles: vehicles.allMarketVehicles,
    vehicleActivities: marketVehicleActivities,
    vehiclesLoading: vehicles.isLoading,
    locations: locations.activeMarketLocations,
    vehicleZones: vehicleZones.vehicleZones,
    pickupZones: pickupZones.activeMarketPickupZones,
    wranglers: wranglers.activeMarketWranglers,
    oneTask: tasks.oneTask,
    isLoading: tasks.isLoading,
    adminUrls: settings.adminUrls,
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(MarketVehicleSummary),
);
